import { string } from 'prop-types';
import React from 'react';
import { useDispatch } from 'react-redux';

import CommonImage from '@components/CommonImage';
import LoaderComponent from '@components/LoaderComponent';

import useTrendResearchFilterParams from '@features/trend-research/hooks/useTrendResearchFilterParams';
import useKeyDesigns from '@features/trend-research/hooks/useTrendResearchKeyDesigns';
import EmptyState from '@features/trend-research/components/common/EmptyState';
import {
	Card,
	CardContent,
	ColorThemeImageContainer,
	TopBar
} from '@features/trend-research/components/common/TrendCard';
import { filterFieldType } from '@features/filter/static/filterUtil';

import { useInsightSummary, useSelectedGroup } from '@store/reducers/trend-research/TrendResearchReducer';
import { storeItemInGlobalFilter } from '@store/actions/FilterAction';
import { setTrendResearchSelectedGroup } from '@store/actions/trend-research/TrendResearchAction';

import { trackMixPanelEvent } from '@services/commonService';

import { processDecimalValue } from '@utils/Util';

const KeyColorOrTheme = ({ title, keyType, summaryField, source, type }) => {
	const summaryCounts = useInsightSummary();
	const removeParamList =
		source === 'Instagram' ? ['fabricTypeList', 'brandCodeList'] : ['instagramUserNameList'];
	const { requestBody } = useTrendResearchFilterParams(removeParamList);
	const { designs, isLoading } = useKeyDesigns(keyType, requestBody, summaryField);
	const dispatch = useDispatch();
	const selectedGroup = useSelectedGroup();

	const renderCards = () => {
		let key = selectedGroup === 'COLOR' ? 'COLOR' : 'TREND_THEME';
		const handleRoute = (item) => {
			let value = type === 'key-color' ? item.colorName : item.themeName;
			trackMixPanelEvent(
				{
					cardName: value,
					groupType: selectedGroup
				},
				'TRENDS_GROUPED_CARD_ON_CLICK'
			);
			dispatch(
				storeItemInGlobalFilter({
					name: filterFieldType[key]['idName'],
					key: value,
					value: value,
					identifier: `${filterFieldType[key]['idName']}_${value}`,
					checked: true
				})
			);
			dispatch(setTrendResearchSelectedGroup('DEFAULT'));
		};

		return designs?.map((item, idx) => {
			const [mainImage, ...images] = item?.imageUrlList || [];
			const contents = {
				title: type === 'key-color' ? item.colorName : item.themeName,
				itemCount: type === 'key-color' ? item.colorCount : item.themeCount,
				percentage: summaryCounts?.noOfProducts
					? processDecimalValue(
							((type === 'key-color' ? item.colorCount : item.themeCount) /
								summaryCounts.noOfProducts) *
								100
						) + '%'
					: `0%`
			};

			return (
				<Card
					className="single-key-color-item cursor-pointer"
					key={`key_${keyType}_${idx}`}
					onClick={() => handleRoute(item)}
				>
					<TopBar topText={idx === 0} />
					<ColorThemeImageContainer
						mainImage={mainImage}
						images={type === 'key-color' ? [mainImage, ...images] : images}
						themeType={type === 'key-color' ? 'COLOR' : 'THEME'}
						colorCode={type === 'key-color' ? item?.colorHexCode?.slice(1, -1) : ''}
					/>
					<CardContent details={contents} tooltipSize={42} />
				</Card>
			);
		});
	};

	return (
		<LoaderComponent loading={isLoading}>
			<div className="key-color-section trend-padding scrollbar-on-hover">
				<div className="section-title pb-3 d-flex align-items-center justify-content-between">
					<p className="regular-16 weight-500 gray_dark_01 d-flex align-items-center">
						<CommonImage
							src={
								type === 'key-color'
									? '/icons/flag/key-color.svg'
									: '/icons/flag/key-theme.svg'
							}
							width={20}
						/>
						<span className="pl-1">{title}</span>{' '}
					</p>
				</div>
				{designs.length > 0 ? (
					<div className="key-color-slider trending-slider">{renderCards()}</div>
				) : (
					<EmptyState />
				)}
			</div>
		</LoaderComponent>
	);
};

KeyColorOrTheme.propTypes = {
	title: string.isRequired,
	source: string.isRequired,
	summaryField: string.isRequired,
	keyType: string.isRequired,
	type: string.isRequired
};

export default KeyColorOrTheme;
